/**
 * Camera Input Handlers
 * L4-ZOOM-006 & L4-ZOOM-007: Mouse wheel zoom, WASD keyboard pan, and pinch-to-zoom
 */

import { CameraManager } from './camera-manager';
import { Point, clamp } from './camera-types';
import { calculateWheelZoom } from './camera-transforms';

/**
 * Create a wheel zoom handler for the camera
 *
 * Zooms toward the cursor position on mouse wheel events.
 *
 * @param cameraManager - The camera manager to control
 * @param canvas - The canvas element for cursor position calculation
 * @returns Event handler function for wheel events
 */
export function createWheelZoomHandler(
  cameraManager: CameraManager,
  canvas: HTMLCanvasElement
): (event: WheelEvent) => void {
  const config = cameraManager.getConfig();

  return (event: WheelEvent) => {
    // Prevent page scroll
    event.preventDefault();

    // Get cursor position relative to canvas
    const rect = canvas.getBoundingClientRect();
    const cursorPos: Point = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };

    // Calculate new zoom level from wheel delta
    const currentZoom = cameraManager.getZoom();
    const targetZoom = calculateWheelZoom(
      event.deltaY,
      currentZoom,
      config.wheelZoomFactor
    );

    // Apply zoom centered on cursor
    cameraManager.zoomAtPoint(targetZoom, cursorPos);
  };
}

/**
 * Keyboard pan handler interface
 */
export interface KeyboardPanHandler {
  handleKeyDown: (event: KeyboardEvent) => void;
  handleKeyUp: (event: KeyboardEvent) => void;
  update: () => void;
  isKeyPressed: (key: string) => boolean;
  destroy: () => void;
}

/**
 * Create a keyboard pan handler for WASD keys
 *
 * Tracks which keys are pressed and applies pan on each update() call.
 *
 * @param cameraManager - The camera manager to control
 * @returns Handler object with keydown, keyup, and update methods
 */
export function createKeyboardPanHandler(
  cameraManager: CameraManager
): KeyboardPanHandler {
  const config = cameraManager.getConfig();

  // Track which keys are currently pressed
  const keysPressed = new Set<string>();

  // Normalize key to lowercase
  const normalizeKey = (key: string) => key.toLowerCase();

  const handleKeyDown = (event: KeyboardEvent): void => {
    const key = normalizeKey(event.key);
    if (['w', 'a', 's', 'd'].includes(key)) {
      keysPressed.add(key);
      event.preventDefault();
    }
  };

  const handleKeyUp = (event: KeyboardEvent): void => {
    const key = normalizeKey(event.key);
    keysPressed.delete(key);
  };

  const update = (): void => {
    if (keysPressed.size === 0) return;

    // Calculate pan speed based on zoom level
    // At higher zoom, move slower in world space (feels more natural)
    const zoom = cameraManager.getZoom();
    const adjustedSpeed = config.panSpeed / zoom;

    let deltaX = 0;
    let deltaY = 0;

    // W = move viewport up (pan positive Y)
    if (keysPressed.has('w')) {
      deltaY += adjustedSpeed;
    }
    // S = move viewport down (pan negative Y)
    if (keysPressed.has('s')) {
      deltaY -= adjustedSpeed;
    }
    // A = move viewport left (pan positive X)
    if (keysPressed.has('a')) {
      deltaX += adjustedSpeed;
    }
    // D = move viewport right (pan negative X)
    if (keysPressed.has('d')) {
      deltaX -= adjustedSpeed;
    }

    if (deltaX !== 0 || deltaY !== 0) {
      cameraManager.pan(deltaX, deltaY);
    }
  };

  const isKeyPressed = (key: string): boolean => {
    return keysPressed.has(normalizeKey(key));
  };

  const destroy = (): void => {
    keysPressed.clear();
  };

  return {
    handleKeyDown,
    handleKeyUp,
    update,
    isKeyPressed,
    destroy,
  };
}

/**
 * Configuration options for touch handler
 */
export interface TouchHandlerConfig {
  /**
   * Distance threshold in pixels before single-finger drag becomes a pan gesture.
   * Below this threshold, touches pass through to underlying elements (e.g., PixiJS).
   * Default: 10 pixels
   */
  dragThreshold?: number;

  /**
   * Whether to enable single-finger panning at all.
   * Set to false to only enable pinch-to-zoom without pan.
   * Default: true
   */
  enablePan?: boolean;
}

/**
 * Touch handler interface (pinch zoom + single finger pan)
 */
export interface TouchHandler {
  handleTouchStart: (event: TouchEvent) => void;
  handleTouchMove: (event: TouchEvent) => void;
  handleTouchEnd: (event: TouchEvent) => void;
  isPinching: () => boolean;
  isPanning: () => boolean;
  isDetecting: () => boolean;
  destroy: () => void;
}

/**
 * @deprecated Use TouchHandler instead
 */
export interface PinchZoomHandler {
  handleTouchStart: (event: TouchEvent) => void;
  handleTouchMove: (event: TouchEvent) => void;
  handleTouchEnd: (event: TouchEvent) => void;
  isPinching: () => boolean;
  destroy: () => void;
}

/**
 * Calculate the distance between two touch points
 */
function getTouchDistance(touch1: Touch, touch2: Touch): number {
  const dx = touch1.clientX - touch2.clientX;
  const dy = touch1.clientY - touch2.clientY;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Calculate the center point between two touches relative to canvas
 */
function getTouchCenter(touch1: Touch, touch2: Touch, canvasRect: DOMRect): Point {
  return {
    x: (touch1.clientX + touch2.clientX) / 2 - canvasRect.left,
    y: (touch1.clientY + touch2.clientY) / 2 - canvasRect.top,
  };
}

/**
 * Create a unified touch handler for mobile devices
 *
 * Handles both:
 * - Single finger drag: pan the camera (with threshold to allow taps through)
 * - Two finger pinch: zoom in/out centered on pinch midpoint
 *
 * The drag threshold feature allows quick taps to pass through to underlying
 * elements (like PixiJS pointer events), while longer drags control the camera.
 *
 * @param cameraManager - The camera manager to control
 * @param canvas - The canvas element for position calculation
 * @param options - Optional configuration for threshold behavior
 * @returns Handler object with touch event methods
 */
export function createTouchHandler(
  cameraManager: CameraManager,
  canvas: HTMLCanvasElement,
  options: TouchHandlerConfig = {}
): TouchHandler {
  const config = cameraManager.getConfig();

  // Default options
  const dragThreshold = options.dragThreshold ?? 10;
  const enablePan = options.enablePan ?? true;

  // Track touch state
  let initialDistance: number | null = null;
  let initialZoom: number | null = null;
  let pinching = false;
  let panning = false;
  let detecting = false; // New state: waiting to see if this is a tap or drag
  let touchStartPos: Point | null = null;
  let lastTouchPos: Point | null = null;

  /**
   * Calculate distance between two points
   */
  const getPointDistance = (p1: Point, p2: Point): number => {
    const dx = p1.x - p2.x;
    const dy = p1.y - p2.y;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = (event: TouchEvent): void => {
    console.log('[Touch] touchstart, touches:', event.touches.length);

    if (event.touches.length === 2) {
      // Start pinch gesture - always preventDefault for pinch
      event.preventDefault();
      pinching = true;
      panning = false;
      detecting = false;
      initialDistance = getTouchDistance(event.touches[0], event.touches[1]);
      initialZoom = cameraManager.getZoom();
      touchStartPos = null;
      lastTouchPos = null;
      console.log('[Touch] Pinch started, distance:', initialDistance, 'zoom:', initialZoom);
    } else if (event.touches.length === 1 && enablePan) {
      // Single finger - enter detecting state
      // DON'T preventDefault yet - allow tap to pass through to PixiJS
      detecting = true;
      panning = false;
      pinching = false;
      touchStartPos = {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY,
      };
      lastTouchPos = { ...touchStartPos };
      console.log('[Touch] Detecting gesture (threshold:', dragThreshold, 'px)');
    }
  };

  const handleTouchMove = (event: TouchEvent): void => {
    if (pinching && event.touches.length === 2 && initialDistance !== null && initialZoom !== null) {
      // Handle pinch zoom
      event.preventDefault();

      const currentDistance = getTouchDistance(event.touches[0], event.touches[1]);
      const rawScale = currentDistance / initialDistance;

      // Amplify pinch sensitivity (4x makes finger movements very responsive)
      const sensitivity = 4.0;
      const amplifiedScale = 1 + (rawScale - 1) * sensitivity;

      // Calculate target zoom (clamped to valid range)
      const targetZoom = clamp(
        initialZoom * amplifiedScale,
        config.minZoom,
        config.maxZoom
      );

      // Get center point for zoom
      const rect = canvas.getBoundingClientRect();
      const centerPoint = getTouchCenter(event.touches[0], event.touches[1], rect);

      console.log('[Touch] Pinching, rawScale:', rawScale.toFixed(2), 'amplified:', amplifiedScale.toFixed(2), 'zoom:', targetZoom.toFixed(2));

      // Apply zoom at pinch center
      cameraManager.zoomAtPoint(targetZoom, centerPoint);
    } else if (detecting && event.touches.length === 1 && touchStartPos !== null) {
      // In detecting state - check if movement exceeds threshold
      const currentPos = {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY,
      };

      const distance = getPointDistance(touchStartPos, currentPos);

      if (distance > dragThreshold) {
        // Movement exceeded threshold - switch to panning state
        event.preventDefault();
        detecting = false;
        panning = true;
        lastTouchPos = currentPos;
        console.log('[Touch] Threshold exceeded (', distance.toFixed(1), 'px), switching to pan mode');
      }
      // If not exceeded, don't preventDefault - let PixiJS handle it
    } else if (panning && event.touches.length === 1 && lastTouchPos !== null) {
      // Handle single finger pan (already committed to panning)
      event.preventDefault();

      const currentPos = {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY,
      };

      // Calculate delta (inverted for natural scrolling feel)
      const deltaX = currentPos.x - lastTouchPos.x;
      const deltaY = currentPos.y - lastTouchPos.y;

      // Apply pan (positive delta = drag right = pan left, so we use positive values)
      cameraManager.pan(deltaX, deltaY);

      // Update last position
      lastTouchPos = currentPos;

      console.log('[Touch] Panning, deltaX:', deltaX.toFixed(1), 'deltaY:', deltaY.toFixed(1));
    }
  };

  const handleTouchEnd = (event: TouchEvent): void => {
    console.log('[Touch] touchend, remaining touches:', event.touches.length);

    if (event.touches.length < 2) {
      pinching = false;
      initialDistance = null;
      initialZoom = null;
    }

    if (event.touches.length === 0) {
      // If we're still in detecting state, the touch ended without exceeding threshold
      // This means it was a tap - we already let PixiJS handle it by not calling preventDefault
      if (detecting) {
        console.log('[Touch] Touch ended in detecting state - tap passed through to PixiJS');
      }

      panning = false;
      detecting = false;
      touchStartPos = null;
      lastTouchPos = null;
    } else if (event.touches.length === 1 && !pinching) {
      // Transitioned from pinch to single finger - enter detecting state
      detecting = true;
      panning = false;
      touchStartPos = {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY,
      };
      lastTouchPos = { ...touchStartPos };
    }
  };

  const isPinching = (): boolean => pinching;
  const isPanning = (): boolean => panning;
  const isDetecting = (): boolean => detecting;

  const destroy = (): void => {
    pinching = false;
    panning = false;
    detecting = false;
    initialDistance = null;
    initialZoom = null;
    touchStartPos = null;
    lastTouchPos = null;
  };

  return {
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    isPinching,
    isPanning,
    isDetecting,
    destroy,
  };
}

/**
 * @deprecated Use createTouchHandler instead
 * Create a pinch-to-zoom handler for touch devices
 */
export function createPinchZoomHandler(
  cameraManager: CameraManager,
  canvas: HTMLCanvasElement
): PinchZoomHandler {
  // Delegate to the new unified handler
  const handler = createTouchHandler(cameraManager, canvas);
  return {
    handleTouchStart: handler.handleTouchStart,
    handleTouchMove: handler.handleTouchMove,
    handleTouchEnd: handler.handleTouchEnd,
    isPinching: handler.isPinching,
    destroy: handler.destroy,
  };
}
